import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { Modal } from "@mui/material";
import NoiseBackground from "../Background/NoiseBackground";
import logo from "../../assets/images/logo/dodeal-white.png";
import { BsFilter } from "react-icons/bs";
import { RxCross1 } from "react-icons/rx";
import {
  PiFacebookLogoDuotone,
  PiInstagramLogoDuotone,
  PiLinkedinLogoDuotone,
  PiSnapchatLogoDuotone,
  PiTiktokLogoDuotone,
} from "react-icons/pi";

const Socials = [
  {
    title: "LinkedIn",
    icon: <PiLinkedinLogoDuotone size={24} />,
    link: "https://www.linkedin.com/company/dodealagency",
  },
  {
    title: "Facebook",
    icon: <PiFacebookLogoDuotone size={24} />,
    link: "https://www.facebook.com/profile.php?id=61569889287329",
  },
  {
    title: "Instagram",
    icon: <PiInstagramLogoDuotone size={24} />,
    link: "https://www.instagram.com/dodeal.agency/",
  },
  {
    title: "TikTok",
    icon: <PiTiktokLogoDuotone size={24} />,
    link: "https://www.tiktok.com/@dodeal.agency",
  },
  {
    title: "Snapchat",
    icon: <PiSnapchatLogoDuotone size={24} />,
    link: "https://web.snapchat.com/?ref=homepage_auto_redirect",
  },
];

const HideNav = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hideNav, setHideNav] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const idleTimeoutRef = useRef(null);
  const closingTimeoutRef = useRef(null);
  const navbarRef = useRef(null);

  const topBarLinks = [
    { path: "/", pageName: "HOME", language: "en" },
    { path: "/about", pageName: "About", language: "en" },
    { path: "/services", pageName: "Services", language: "en" },
    { path: "/web-mobile-apps", pageName: "Enquire us", language: "en" },
    { path: "/blogs", pageName: "Insights", language: "en" }
  ];

  // Handle modal close
  const handleClose = () => {
    setIsClosing(true);
    closingTimeoutRef.current = setTimeout(() => {
      setIsClosing(false);
      setMenuOpen(false);
    }, 1000);
  };

  // Cleanup closing timeout
  useEffect(() => {
    return () => clearTimeout(closingTimeoutRef.current);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Idle detection for homepage only
  const resetIdleTimeout = (event) => {
    // Skip idle detection for non-home pages
    if (location.pathname !== "/") {
      setHideNav(false);
      return;
    }

    // Check if mouse is over navbar
    const isOverNavbar = navbarRef.current && navbarRef.current.contains(event?.target);
    if (isOverNavbar) {
      setHideNav(false); // Keep navbar visible when hovering
      return;
    }

    clearTimeout(idleTimeoutRef.current);
    setHideNav(true); // Hide navbar during interaction
    idleTimeoutRef.current = setTimeout(() => {
      setHideNav(false); // Show navbar after idle
    }, 3000);
  };

  // Handle interactions for homepage
  useEffect(() => {
    // Always show navbar on non-home pages
    if (location.pathname !== "/") {
      setHideNav(false);
      return;
    }

    const handleInteraction = (event) => resetIdleTimeout(event);

    // Initial hide and idle setup for homepage
    resetIdleTimeout();

    // Add event listeners for interactions
    window.addEventListener("scroll", handleInteraction);
    window.addEventListener("mousemove", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);

    return () => {
      clearTimeout(idleTimeoutRef.current);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("mousemove", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
    };
  }, [location.pathname]);

  // GSAP animation for navbar
  useEffect(() => {
    gsap.to(".navbar", {
      opacity: hideNav ? 0 : 1,
      x: (!isMobile || location.pathname !== "/") && hideNav ? "-100%" : "0%",
      y: (isMobile || location.pathname !== "/") && hideNav ? "-100%" : "0%",
      duration: 0.5,
      ease: "power2.out",
    });
  }, [hideNav, isMobile, location.pathname]);

  return (
    <>
      <div
        ref={navbarRef}
        className={`z-[105] backdrop-blur-md navbar fixed p-2 transition-all duration-300 flex top-0 ${isMobile || location.pathname !== "/"
            ? "w-full"
            : "flex w-28 lg:w-30 left-0 h-full items-center justify-center"
          }`}
      >
        {isMobile || location.pathname !== "/" ? (
          <div className="flex w-full justify-between items-center gap-5 padding-container">
            <img src={logo} alt="DODEAL" className="w-[70px]" />
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-white rounded-full w-fit aspect-square bg-white/20 bg-blur-10 p-1 hover:bg-primary"
            >
              <BsFilter size={22} />
            </button>
          </div>
        ) : (
          <div className="text-white">
            <nav
              className="relative flex flex-col gap-2 py-5 items-center transition-all duration-300"
              style={{ zIndex: 2 }}
            >
              <span
                className="absolute top-0 left-1/2 h-full w-2 opacity-50 rounded-full bg-[#375a69] transform -translate-x-1/2 transition-all duration-300"
                style={{ transformOrigin: "center", zIndex: -1 }}
              ></span>
              {topBarLinks.map((link, index) => (
                <NavLink
                  key={index}
                  to={link.path}
                  className={`transition-all cursor-pointer duration-300 ${location.pathname === link.path
                      ? "text-white drop-shadow-lg font-bold text-lg"
                      : "text-slate-300 opacity-70 font-semibold text-sm hover:opacity-100 hover:font-bold"
                    }`}
                >
                  {link.pageName}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>

      {menuOpen && (
        <Modal
          open={menuOpen}
          onClose={handleClose}
          aria-labelledby="keep-mounted-modal-title"
          aria-describedby="keep-mounted-modal-description"
          closeAfterTransition
        >
          <div
            className={`w-[100vw] min-h-[100vh] overflow-hidden relative bg-[var(--secondary)] modal-open-right ${isClosing ? "modal-close-right" : "modal-open-right"
              }`}
          >
            <NoiseBackground />
            <div className="fixed top-1/2 left-2/3 lg:left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center my-5 py-5">
              <div
                className="w-[100px] h-[200px] lg:w-[150px] lg:h-[300px] rounded-r-full"
                style={{
                  backgroundImage:
                    "linear-gradient(to bottom, var(--secondary), var(--primary))",
                  boxShadow:
                    "inset 6px 6px 4px rgba(255,255,255,0.25), inset -6px -6px 8px rgba(0,0,0,0.2)",
                  zIndex: -1,
                }}
              ></div>
              <div
                className="w-[100px] h-[200px] lg:w-[150px] lg:h-[300px] rounded-r-full"
                style={{
                  backgroundImage:
                    "linear-gradient(to bottom, var(--secondary), var(--primary))",
                  boxShadow:
                    "inset 6px 6px 4px rgba(255,255,255,0.25), inset -6px -6px 8px rgba(0,0,0,0.2)",
                  zIndex: -1,
                }}
              ></div>
            </div>

            <div className="min-h-[85vh] overflow-y-scroll flex flex-col justify-between py-5 my-5">
              <div className="w-full padding-container flex items-center justify-between gap-5">
                <img src={logo} alt="DoDeal" className="w-[80px]" />
                <RxCross1
                  size={28}
                  className="p-1.5 rounded-full border border-gray-500 text-gray-500 z-50 cursor-pointer"
                  onClick={handleClose}
                />
              </div>
              <div className="relative padding-container grid grid-cols-1 lg:grid-cols-2 my-5 py-5">
                <div
                  className="flex flex-col justify-center gap-5 my-5 py-5"
                  style={{ zIndex: 12 }}
                >
                  {topBarLinks.map((link, index) => (
                    <NavLink
                      key={index}
                      to={link.path}
                      onClick={handleClose}
                      className={`transition-all text-start text-2xl md:text-3xl lg:text-4xl duration-300 uppercase font-bold ${location.pathname === link.path
                          ? "text-white opacity-100"
                          : "text-slate-300 opacity-70 hover:opacity-90 gradient-fade"
                        }`}
                    >
                      {link.pageName}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div
                className="w-full padding-container flex flex-col items-center justify-center"
                style={{ zIndex: 12 }}
              >
                <div className="mb-4">contact@dodeal.com</div>
                <div className="flex items-center justify-center gap-4 pb-5 mb-5">
                  {Socials.map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-[20px] aspect-square flex items-center justify-center p-1"
                      style={{
                        background:
                          "linear-gradient(149.54deg, rgba(211, 211, 211, 0.2) 22.72%, rgba(255, 255, 255, 0.5) 115.21%)",
                      }}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default HideNav;